/* ImportWizard — Asistente de Migración Inteligente (FASE 4B.2)
 *
 * 5 pasos:
 *   1. Fuente (Excel/CSV) — cards
 *   2. Archivo — dropzone
 *   3. Mapeo de columnas — auto-detección con confidence + edición manual + defaults
 *   4. Vista previa — dry-run + resumen + tabla de errores
 *   5. Resultado — execute + estadísticas finales
 *
 * Diseñado para funcionar EXACTAMENTE igual con futuras fuentes
 * (Wasi/Tokko/Inmovilla) — solo cambia el paso 2 (o se salta cuando el
 * source no acepta upload; en su lugar recolecta credenciales).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, CheckCircle2, AlertTriangle, XCircle, FileSpreadsheet,
  FileText, Upload, Loader2, Sparkles, Info, RotateCcw, ChevronRight, Play, Rocket,
} from 'lucide-react';
import { api, friendlyError } from '../lib/api';
import { useToast } from '../hooks/use-toast';

// ─────────────────────────────────────── Step indicator

const STEPS = [
  { key: 'source',   label: 'Fuente'      },
  { key: 'file',     label: 'Archivo'     },
  { key: 'mapping',  label: 'Mapeo'       },
  { key: 'preview',  label: 'Vista previa'},
  { key: 'result',   label: 'Importar'    },
];

function StepBar({ current }) {
  return (
    <ol className="flex items-center gap-1 sm:gap-2 mb-6" data-testid="import-step-bar">
      {STEPS.map((s, i) => {
        const active = i === current;
        const done = i < current;
        return (
          <li key={s.key} className="flex items-center gap-1 sm:gap-2 flex-1" data-testid={`import-step-${s.key}`}>
            <div className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center text-[11px] font-semibold border ${
              done ? 'bg-habita text-white border-habita' : active ? 'bg-white text-habita border-habita' : 'bg-gray-100 text-gray-400 border-gray-200'
            }`}>
              {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
            </div>
            <span className={`text-[11px] sm:text-xs font-medium ${active ? 'text-habita-dark' : done ? 'text-gray-600' : 'text-gray-400'}`}>{s.label}</span>
            {i < STEPS.length - 1 && <ChevronRight className="w-3 h-3 text-gray-300 hidden sm:block ml-1" />}
          </li>
        );
      })}
    </ol>
  );
}

// ─────────────────────────────────────── Step 1 — Source selection

function StepSource({ sources, selected, onSelect, onNext }) {
  const iconFor = (t) => t === 'excel' ? FileSpreadsheet : FileText;
  return (
    <div>
      <h2 className="font-serif text-2xl font-bold text-habita-dark mb-2">Elige la fuente</h2>
      <p className="text-sm text-gray-500 mb-5">Habita procesa cada fuente con el mismo motor universal. Puedes agregar Wasi / Tokko / Inmovilla más adelante sin re-mapear.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {sources.map(s => {
          const Icon = iconFor(s.source_type);
          const isSel = selected?.source_type === s.source_type;
          return (
            <button
              key={s.source_type}
              onClick={() => onSelect(s)}
              className={`text-left p-5 rounded-2xl border-2 transition-all ${isSel ? 'border-habita bg-habita/5 shadow-lg' : 'border-gray-100 bg-white hover:border-habita/40'}`}
              data-testid={`source-card-${s.source_type}`}
            >
              <div className="flex items-center gap-3 mb-2">
                <Icon className={`w-6 h-6 ${isSel ? 'text-habita' : 'text-gray-500'}`} />
                <p className="font-semibold text-gray-900">{s.display_name}</p>
              </div>
              <p className="text-xs text-gray-500">Extensiones: {s.accepts_extensions.join(', ')}</p>
              {isSel && <div className="mt-3 inline-flex items-center gap-1 text-xs text-habita font-medium"><CheckCircle2 className="w-3 h-3" /> Seleccionado</div>}
            </button>
          );
        })}
      </div>
      <div className="mt-8 flex justify-end">
        <button
          onClick={onNext}
          disabled={!selected}
          className="h-11 px-6 rounded-full bg-habita text-white font-semibold hover:bg-habita-dark disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
          data-testid="import-next-source"
        >
          Continuar <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────── Step 2 — File upload (dropzone)

function StepFile({ source, onUploaded, onBack }) {
  const { toast } = useToast();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const onPick = () => inputRef.current?.click();
  const onFile = (f) => {
    if (!f) return;
    const ext = '.' + f.name.split('.').pop().toLowerCase();
    if (!source.accepts_extensions.includes(ext)) {
      toast({ title: 'Extensión no permitida', description: `Usa ${source.accepts_extensions.join(', ')}`, variant: 'destructive' });
      return;
    }
    setFile(f);
  };
  const onDrop = (e) => { e.preventDefault(); if (e.dataTransfer.files?.[0]) onFile(e.dataTransfer.files[0]); };

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await api.importUpload(source.source_type, file);
      toast({ title: 'Archivo procesado', description: `${res.total_rows} filas detectadas` });
      onUploaded(res);
    } catch (err) {
      toast({ title: 'No pudimos leer el archivo', description: friendlyError(err), variant: 'destructive' });
    } finally { setUploading(false); }
  };

  return (
    <div>
      <h2 className="font-serif text-2xl font-bold text-habita-dark mb-2">Sube tu archivo {source.display_name}</h2>
      <p className="text-sm text-gray-500 mb-5">Detectaremos automáticamente las columnas y te dejaremos ajustar el mapeo antes de importar.</p>

      <div
        onDrop={onDrop}
        onDragOver={e => e.preventDefault()}
        onClick={onPick}
        className="border-2 border-dashed border-gray-200 rounded-2xl p-8 sm:p-12 text-center cursor-pointer hover:border-habita hover:bg-habita/5 transition-colors"
        data-testid="import-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          accept={source.accepts_extensions.join(',')}
          className="hidden"
          onChange={e => onFile(e.target.files?.[0])}
          data-testid="import-file-input"
        />
        <Upload className="w-10 h-10 text-habita mx-auto mb-3" />
        {file ? (
          <>
            <p className="font-semibold text-gray-900">{file.name}</p>
            <p className="text-xs text-gray-500 mt-1">{(file.size / 1024).toFixed(1)} KB · Haz clic para cambiar</p>
          </>
        ) : (
          <>
            <p className="font-semibold text-gray-900">Arrastra tu archivo aquí</p>
            <p className="text-xs text-gray-500 mt-1">o haz clic para seleccionarlo · máx 8 MB · {source.accepts_extensions.join(' / ')}</p>
          </>
        )}
      </div>

      <div className="mt-8 flex justify-between">
        <button onClick={onBack} className="h-11 px-5 rounded-full bg-white text-gray-700 border border-gray-200 hover:border-gray-400 font-medium flex items-center gap-2" data-testid="import-back-file">
          <ArrowLeft className="w-4 h-4" /> Volver
        </button>
        <button
          onClick={upload}
          disabled={!file || uploading}
          className="h-11 px-6 rounded-full bg-habita text-white font-semibold hover:bg-habita-dark disabled:opacity-40 flex items-center gap-2"
          data-testid="import-upload-btn"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Analizar archivo
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────── Step 3 — Column mapping

function ConfidenceBadge({ value }) {
  if (value == null) return null;
  const pct = Math.round(value * 100);
  const cls = value >= 0.9 ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : value >= 0.6 ? 'bg-amber-50 text-amber-700 border-amber-200'
    : 'bg-gray-50 text-gray-600 border-gray-200';
  return <span className={`text-[10px] px-1.5 py-0.5 rounded border ${cls}`} data-testid="confidence-badge">{pct}%</span>;
}

function StepMapping({ job, canonicalFields, onNext, onBack }) {
  const { toast } = useToast();
  const [mapping, setMapping] = useState({ ...(job.mapping || {}) });
  const [defaults, setDefaults] = useState(job.defaults || {});
  const [saving, setSaving] = useState(false);

  // Which canonical fields are already mapped (so we can hide them from other selectors' options)
  const usedCanonical = useMemo(() => new Set(Object.values(mapping).filter(Boolean)), [mapping]);

  const setColMap = (col, canonical) => {
    setMapping(prev => ({ ...prev, [col]: canonical || undefined }));
  };

  // Warn when a required field is missing AND no default is set for it
  const missingRequired = useMemo(() => {
    const requiredKeys = canonicalFields.filter(f => f.required).map(f => f.key);
    const mappedCanonical = new Set(Object.values(mapping).filter(Boolean));
    return requiredKeys.filter(k => !mappedCanonical.has(k) && !defaults[k]);
  }, [mapping, defaults, canonicalFields]);

  const next = async () => {
    if (missingRequired.length > 0) {
      toast({
        title: 'Faltan campos requeridos',
        description: `Mapea o define un valor por defecto para: ${missingRequired.join(', ')}`,
        variant: 'destructive',
      });
      return;
    }
    setSaving(true);
    try {
      // Strip undefined values before sending
      const cleanMapping = Object.fromEntries(
        Object.entries(mapping).filter(([, v]) => v),
      );
      const cleanDefaults = Object.fromEntries(
        Object.entries(defaults).filter(([, v]) => v),
      );
      const updated = await api.importSaveMapping(job.id, cleanMapping, cleanDefaults);
      onNext(updated);
    } catch (err) {
      toast({ title: 'Error', description: friendlyError(err), variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const detection = job.detection || {};
  const columns = detection.columns || [];
  const confidence = detection.confidence || {};
  const samples = detection.samples || {};

  return (
    <div>
      <h2 className="font-serif text-2xl font-bold text-habita-dark mb-2">Mapea las columnas</h2>
      <p className="text-sm text-gray-500 mb-4">Detectamos automáticamente las columnas. Ajusta si algo no coincide. Los campos con <span className="text-rose-600">*</span> son obligatorios.</p>

      {missingRequired.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-xs text-amber-800 flex gap-2" data-testid="mapping-missing-alert">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <div>
            Faltan campos requeridos: <strong>{missingRequired.join(', ')}</strong>. Mapéalos o define un valor por defecto abajo.
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
        <div className="max-h-[420px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="text-left p-3 font-semibold text-gray-700 w-[35%]">Columna en tu archivo</th>
                <th className="text-left p-3 font-semibold text-gray-700 w-[35%]">Campo en Habita</th>
                <th className="text-left p-3 font-semibold text-gray-700">Muestra</th>
              </tr>
            </thead>
            <tbody>
              {columns.map(col => {
                const canonical = mapping[col];
                const availableFields = canonicalFields.filter(f => !usedCanonical.has(f.key) || f.key === canonical);
                return (
                  <tr key={col} className="border-t border-gray-50" data-testid={`mapping-row-${col}`}>
                    <td className="p-3 align-top">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-800 break-all">{col}</span>
                        <ConfidenceBadge value={confidence[col]} />
                      </div>
                    </td>
                    <td className="p-3 align-top">
                      <select
                        value={canonical || ''}
                        onChange={e => setColMap(col, e.target.value)}
                        className="w-full h-9 px-2 rounded-lg border border-gray-200 focus:border-habita outline-none text-sm bg-white"
                        data-testid={`mapping-select-${col}`}
                      >
                        <option value="">— Ignorar columna —</option>
                        {availableFields.map(f => (
                          <option key={f.key} value={f.key}>{f.label}{f.required ? ' *' : ''}</option>
                        ))}
                      </select>
                    </td>
                    <td className="p-3 align-top text-xs text-gray-500">
                      {(samples[col] || []).slice(0, 3).map((v, i) => (
                        <div key={i} className="truncate max-w-[220px]">{v == null || v === '' ? <em className="text-gray-300">(vacío)</em> : String(v)}</div>
                      ))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Defaults for required fields not mapped */}
      <details className="mt-4 group" data-testid="mapping-defaults">
        <summary className="cursor-pointer text-sm font-medium text-habita hover:text-habita-dark flex items-center gap-2">
          <Sparkles className="w-4 h-4" /> Valores por defecto (opcional)
        </summary>
        <p className="text-xs text-gray-500 mt-2 mb-3">Aplican a filas sin valor en la columna correspondiente. Útil si tu archivo no incluye operación o tipo.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Operación por defecto</label>
            <select
              value={defaults.operation || ''}
              onChange={e => setDefaults(d => ({ ...d, operation: e.target.value }))}
              className="w-full h-9 px-2 rounded-lg border border-gray-200 focus:border-habita outline-none text-sm bg-white"
              data-testid="default-operation"
            >
              <option value="">— Sin default —</option>
              <option value="venta">Venta</option>
              <option value="arriendo">Arriendo</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Tipo por defecto</label>
            <select
              value={defaults.type || ''}
              onChange={e => setDefaults(d => ({ ...d, type: e.target.value }))}
              className="w-full h-9 px-2 rounded-lg border border-gray-200 focus:border-habita outline-none text-sm bg-white"
              data-testid="default-type"
            >
              <option value="">— Sin default —</option>
              <option value="casa">Casa</option>
              <option value="apartamento">Apartamento</option>
              <option value="apartaestudio">Apartaestudio</option>
              <option value="local">Local</option>
              <option value="oficina">Oficina</option>
              <option value="bodega">Bodega</option>
              <option value="lote">Lote</option>
              <option value="finca">Finca</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Ciudad por defecto</label>
            <input
              type="text"
              value={defaults.city || ''}
              onChange={e => setDefaults(d => ({ ...d, city: e.target.value }))}
              placeholder="Ej: Bogotá"
              className="w-full h-9 px-3 rounded-lg border border-gray-200 focus:border-habita outline-none text-sm"
              data-testid="default-city"
            />
          </div>
        </div>
      </details>

      <div className="mt-8 flex justify-between">
        <button onClick={onBack} className="h-11 px-5 rounded-full bg-white text-gray-700 border border-gray-200 hover:border-gray-400 font-medium flex items-center gap-2" data-testid="import-back-mapping">
          <ArrowLeft className="w-4 h-4" /> Volver
        </button>
        <button
          onClick={next}
          disabled={saving}
          className="h-11 px-6 rounded-full bg-habita text-white font-semibold hover:bg-habita-dark disabled:opacity-40 flex items-center gap-2"
          data-testid="import-next-mapping"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Validar
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────── Step 4 — Preview / dry-run

function SummaryPills({ s }) {
  if (!s) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4" data-testid="import-summary-pills">
      <div className="bg-white border border-gray-100 rounded-xl p-3">
        <p className="text-[10px] text-gray-500 uppercase tracking-widest">Total</p>
        <p className="text-xl font-bold tabular-nums text-gray-900" data-testid="summary-total">{s.total_rows}</p>
      </div>
      <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3">
        <p className="text-[10px] text-emerald-700 uppercase tracking-widest">Válidas</p>
        <p className="text-xl font-bold tabular-nums text-emerald-700" data-testid="summary-valid">{s.valid}</p>
      </div>
      <div className="bg-rose-50 border border-rose-100 rounded-xl p-3">
        <p className="text-[10px] text-rose-700 uppercase tracking-widest">Con errores</p>
        <p className="text-xl font-bold tabular-nums text-rose-700" data-testid="summary-invalid">{s.invalid}</p>
      </div>
      <div className="bg-amber-50 border border-amber-100 rounded-xl p-3">
        <p className="text-[10px] text-amber-700 uppercase tracking-widest">A actualizar</p>
        <p className="text-xl font-bold tabular-nums text-amber-700" data-testid="summary-updated">{s.updated || 0}</p>
      </div>
      <div className="bg-habita/10 border border-habita/20 rounded-xl p-3">
        <p className="text-[10px] text-habita uppercase tracking-widest">A insertar</p>
        <p className="text-xl font-bold tabular-nums text-habita" data-testid="summary-inserted">{s.inserted || 0}</p>
      </div>
    </div>
  );
}

function StepPreview({ job, onRunDryRun, onNext, onBack, dryRun, running }) {
  useEffect(() => {
    if (!dryRun) onRunDryRun();
     
  }, []);
  const s = dryRun?.summary;
  const preview = dryRun?.preview || [];
  return (
    <div>
      <h2 className="font-serif text-2xl font-bold text-habita-dark mb-2">Vista previa</h2>
      <p className="text-sm text-gray-500 mb-4">Simulamos la importación sin guardar nada aún. Revisa el resumen y los errores por fila.</p>

      {running ? (
        <div className="text-center py-12 text-gray-400"><Loader2 className="w-8 h-8 mx-auto animate-spin" /><p className="mt-2 text-sm">Validando filas…</p></div>
      ) : !s ? (
        <p className="text-sm text-gray-400 italic">Sin resultados de validación.</p>
      ) : (
        <>
          <SummaryPills s={s} />

          {(s.errors?.length || 0) > 0 && (
            <div className="bg-white border border-rose-100 rounded-2xl p-4 mb-4" data-testid="preview-errors-panel">
              <h4 className="font-semibold text-rose-700 text-sm mb-2 flex items-center gap-1"><XCircle className="w-4 h-4" /> Errores ({s.errors.length})</h4>
              <ul className="text-xs text-gray-700 space-y-1 max-h-40 overflow-y-auto">
                {s.errors.slice(0, 30).map((e, i) => (
                  <li key={i}><strong className="text-rose-600">Fila {e.row}:</strong> {e.message}</li>
                ))}
                {s.errors.length > 30 && <li className="text-gray-400 italic">… {s.errors.length - 30} más</li>}
              </ul>
            </div>
          )}

          {(s.warnings?.length || 0) > 0 && (
            <div className="bg-white border border-amber-100 rounded-2xl p-4 mb-4" data-testid="preview-warnings-panel">
              <h4 className="font-semibold text-amber-700 text-sm mb-2 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Advertencias ({s.warnings.length})</h4>
              <ul className="text-xs text-gray-600 space-y-1 max-h-32 overflow-y-auto">
                {s.warnings.slice(0, 20).map((w, i) => (
                  <li key={i}><strong>Fila {w.row}:</strong> {w.message}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Sample preview rows */}
          {preview.length > 0 && (
            <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden" data-testid="preview-rows-table">
              <div className="p-3 border-b border-gray-100 bg-gray-50 text-xs font-medium text-gray-600">Primeras filas normalizadas</div>
              <div className="overflow-x-auto max-h-[280px]">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-2 font-semibold text-gray-600">#</th>
                      <th className="text-left p-2 font-semibold text-gray-600">Estado</th>
                      <th className="text-left p-2 font-semibold text-gray-600">Título</th>
                      <th className="text-left p-2 font-semibold text-gray-600">Ciudad</th>
                      <th className="text-left p-2 font-semibold text-gray-600">Op.</th>
                      <th className="text-left p-2 font-semibold text-gray-600">Precio</th>
                      <th className="text-left p-2 font-semibold text-gray-600">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((p, i) => (
                      <tr key={i} className="border-t border-gray-50" data-testid={`preview-row-${i}`}>
                        <td className="p-2 tabular-nums text-gray-500">{p.index + 1}</td>
                        <td className="p-2">
                          {p.errors?.length ? (
                            <span className="inline-flex items-center gap-1 text-rose-600"><XCircle className="w-3 h-3" /> Error</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle2 className="w-3 h-3" /> OK</span>
                          )}
                        </td>
                        <td className="p-2 max-w-[180px] truncate">{p.normalized?.title || <em className="text-gray-300">—</em>}</td>
                        <td className="p-2">{p.normalized?.city || <em className="text-gray-300">—</em>}</td>
                        <td className="p-2 capitalize">{p.normalized?.operation || <em className="text-gray-300">—</em>}</td>
                        <td className="p-2 tabular-nums">{p.normalized?.price ? p.normalized.price.toLocaleString('es-CO') : <em className="text-gray-300">—</em>}</td>
                        <td className="p-2">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                            p.action === 'insert' ? 'bg-emerald-50 text-emerald-700' :
                            p.action === 'update' ? 'bg-amber-50 text-amber-700' :
                            'bg-gray-100 text-gray-500'
                          }`}>{p.action}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      <div className="mt-8 flex justify-between">
        <button onClick={onBack} className="h-11 px-5 rounded-full bg-white text-gray-700 border border-gray-200 hover:border-gray-400 font-medium flex items-center gap-2" data-testid="import-back-preview">
          <ArrowLeft className="w-4 h-4" /> Ajustar mapeo
        </button>
        <div className="flex gap-2">
          <button
            onClick={onRunDryRun}
            disabled={running}
            className="h-11 px-5 rounded-full bg-white text-gray-700 border border-gray-200 hover:border-gray-400 font-medium flex items-center gap-2"
            data-testid="import-rerun-preview"
          >
            <RotateCcw className="w-4 h-4" /> Revalidar
          </button>
          <button
            onClick={onNext}
            disabled={running || !s || s.valid === 0}
            className="h-11 px-6 rounded-full bg-habita text-white font-semibold hover:bg-habita-dark disabled:opacity-40 flex items-center gap-2"
            data-testid="import-next-preview"
          >
            <Rocket className="w-4 h-4" /> Ejecutar importación
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────── Step 5 — Result

function StepResult({ result, running, onRestart, onBack }) {
  const s = result?.summary;
  return (
    <div>
      <h2 className="font-serif text-2xl font-bold text-habita-dark mb-2">Importación completada</h2>
      <p className="text-sm text-gray-500 mb-5">Las propiedades importadas quedan en estado <strong>pendiente</strong> hasta que las moderemos.</p>

      {running ? (
        <div className="text-center py-12 text-gray-400" data-testid="import-executing">
          <Loader2 className="w-8 h-8 mx-auto animate-spin" />
          <p className="mt-2 text-sm">Guardando propiedades…</p>
        </div>
      ) : s ? (
        <>
          <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-5 mb-4" data-testid="result-success-banner">
            <div className="flex items-center gap-2 text-emerald-700 mb-2">
              <CheckCircle2 className="w-6 h-6" />
              <p className="font-semibold text-lg">¡Éxito! Se procesaron {s.total_rows} filas</p>
            </div>
            <p className="text-xs text-emerald-700/80">Insertadas: <strong>{s.inserted}</strong> · Actualizadas: <strong>{s.updated}</strong> · Omitidas: <strong>{s.skipped}</strong> · Errores: <strong>{s.invalid}</strong></p>
          </div>

          {Object.keys(s.stats_by_city || {}).length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <StatsCard title="Por ciudad" data={s.stats_by_city} testid="stats-by-city" />
              <StatsCard title="Por tipo" data={s.stats_by_type} testid="stats-by-type" />
              <StatsCard title="Por operación" data={s.stats_by_operation} testid="stats-by-op" />
            </div>
          )}

          {(s.errors?.length || 0) > 0 && (
            <div className="bg-white border border-rose-100 rounded-2xl p-4 mb-4">
              <h4 className="font-semibold text-rose-700 text-sm mb-2">Filas con errores ({s.errors.length})</h4>
              <ul className="text-xs text-gray-700 space-y-1 max-h-40 overflow-y-auto">
                {s.errors.slice(0, 20).map((e, i) => <li key={i}><strong>Fila {e.row}:</strong> {e.message}</li>)}
              </ul>
            </div>
          )}

          <div className="flex items-center gap-2 text-xs text-gray-500 mt-2">
            <Info className="w-3.5 h-3.5" />
            <span>Duración: {s.duration_ms} ms</span>
          </div>
        </>
      ) : (
        <p className="text-sm text-gray-400 italic">Sin resultados aún.</p>
      )}

      <div className="mt-8 flex flex-wrap gap-3 justify-between">
        <button onClick={onBack} className="h-11 px-5 rounded-full bg-white text-gray-700 border border-gray-200 hover:border-gray-400 font-medium flex items-center gap-2" data-testid="import-back-result">
          <ArrowLeft className="w-4 h-4" /> Volver a vista previa
        </button>
        <div className="flex gap-2">
          <button onClick={onRestart} className="h-11 px-5 rounded-full bg-white text-habita border border-habita hover:bg-habita/5 font-medium flex items-center gap-2" data-testid="import-restart">
            Importar otro archivo
          </button>
          <Link to="/crm/integraciones" className="h-11 px-6 rounded-full bg-habita text-white font-semibold hover:bg-habita-dark flex items-center gap-2" data-testid="import-back-center">
            Ir al Centro
          </Link>
        </div>
      </div>
    </div>
  );
}

function StatsCard({ title, data, testid }) {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]).slice(0, 6);
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-4" data-testid={testid}>
      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">{title}</h4>
      {entries.length === 0 ? <p className="text-xs text-gray-400 italic">Sin datos</p> : (
        <ul className="space-y-1">
          {entries.map(([k, v]) => (
            <li key={k} className="flex items-center justify-between text-sm">
              <span className="text-gray-800 capitalize truncate">{k}</span>
              <span className="text-gray-900 font-semibold tabular-nums">{v}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─────────────────────────────────────── Main wizard

export default function ImportWizard() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [sources, setSources] = useState([]);
  const [canonicalFields, setCanonicalFields] = useState([]);
  const [selectedSource, setSelectedSource] = useState(null);
  const [job, setJob] = useState(null);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [executeResult, setExecuteResult] = useState(null);
  const [dryRunRunning, setDryRunRunning] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.importSources();
        setSources(res.sources || []);
        setCanonicalFields(res.canonical_fields || []);
      } catch (err) {
        toast({ title: 'No pudimos cargar', description: friendlyError(err), variant: 'destructive' });
        navigate('/crm/integraciones');
      } finally { setLoading(false); }
    })();
     
  }, []);

  const runDryRun = useCallback(async () => {
    if (!job) return;
    setDryRunRunning(true);
    try {
      const res = await api.importDryRun(job.id);
      setDryRunResult(res);
    } catch (err) {
      toast({ title: 'Error al validar', description: friendlyError(err), variant: 'destructive' });
    } finally { setDryRunRunning(false); }
  }, [job, toast]);

  const runExecute = useCallback(async () => {
    if (!job) return;
    setExecuting(true);
    try {
      const res = await api.importExecute(job.id);
      setExecuteResult(res);
    } catch (err) {
      toast({ title: 'Error al importar', description: friendlyError(err), variant: 'destructive' });
    } finally { setExecuting(false); }
  }, [job, toast]);

  const restart = () => {
    setSelectedSource(null);
    setJob(null);
    setDryRunResult(null);
    setExecuteResult(null);
    setStep(0);
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="import-wizard">
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-[1100px] mx-auto px-6 py-6">
          <Link to="/crm/integraciones" className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-habita mb-3">
            <ArrowLeft className="w-4 h-4" /> Volver al Centro de Integraciones
          </Link>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 text-habita" />
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-habita">Asistente Universal · FASE 4B.2</span>
          </div>
          <h1 className="font-serif text-3xl font-bold text-habita-dark">Asistente de Migración Inteligente</h1>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">Sube tu inventario desde Excel o CSV. Mismo motor listo para conectar Wasi, Tokko, Inmovilla y más — sin duplicar lógica.</p>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-6 py-8">
        <StepBar current={step} />

        {loading ? (
          <div className="text-center py-16 text-gray-400"><Loader2 className="w-8 h-8 mx-auto animate-spin" /></div>
        ) : (
          <div className="bg-white border border-gray-100 rounded-2xl p-6 sm:p-8" data-testid={`import-step-content-${STEPS[step].key}`}>
            {step === 0 && (
              <StepSource
                sources={sources.filter(s => s.accepts_upload)}
                selected={selectedSource}
                onSelect={setSelectedSource}
                onNext={() => setStep(1)}
              />
            )}
            {step === 1 && selectedSource && (
              <StepFile
                source={selectedSource}
                onUploaded={(j) => { setJob(j); setStep(2); }}
                onBack={() => setStep(0)}
              />
            )}
            {step === 2 && job && (
              <StepMapping
                job={job}
                canonicalFields={canonicalFields}
                onNext={(updated) => { setJob(updated); setDryRunResult(null); setStep(3); }}
                onBack={() => setStep(1)}
              />
            )}
            {step === 3 && job && (
              <StepPreview
                job={job}
                dryRun={dryRunResult}
                running={dryRunRunning}
                onRunDryRun={runDryRun}
                onNext={async () => { setStep(4); await runExecute(); }}
                onBack={() => setStep(2)}
              />
            )}
            {step === 4 && (
              <StepResult
                result={executeResult}
                running={executing}
                onRestart={restart}
                onBack={() => setStep(3)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
